/* global google */
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api'
import React, { useEffect, useRef, useState } from 'react'
import proj4 from 'proj4'
import { toastSweetAlert } from '../Helpers/ToastSweetAlert'
import { GET_ALL_JURISDICTIONS_AND_SUBS } from '../../graphql/Catalog/Jurisdictions/jurisdictions'
import { useQuery } from '@apollo/client'
import Loader from './Spinner'
import GooglePlacesSearch from '../Admin/Catalogs/ReportWeb/components/Location/helpers/GooglePlacesSearch'
import GoogleMapZonePolygon from './GoogleMapZonePolygon'
import { unstable_batchedUpdates } from 'react-dom'

const API_KEY = process.env.REACT_APP_API_KEY_GOOGLE_MAPS
const urlGeoJson = '/geomatica/zonas_tlajomulco.geojson'
const urlGeoJsonColonies = '/geomatica/colonias_tlajomulco.geojson'
const libraries = ['places', 'geometry', 'kml']

// Configuración de estilos y opciones del mapa
const DEFAULT_MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
}
const DEFAULT_MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
]
const DEFAULT_CENTER = { lat: 20.473623708878552, lng: -103.44747586737101 }

// Define el sistema de coordenadas UTM (por ejemplo, zona 13N para Tlajomulco)
const utmZone13N = '+proj=utm +zone=13 +datum=WGS84 +units=m +no_defs'

const GoogleMapComponentV2 = ({
  //PROPS FOR GOOGLE MAP
  title = 'Google Map',
  mapContainerStyle = { width: '100%', height: '400px' },
  optionsGoogleMap = DEFAULT_MAP_OPTIONS,
  defaultCenterGoogleMap = DEFAULT_CENTER,
  stylesGoogleMap = DEFAULT_MAP_STYLES,
  boundsGoogleMap = [],
  fieldsAutocomplete = [
    'formatted_address',
    'geometry',
    'name',
    'address_components',
    'url',
  ],
  pathsPolygon = false,
  //THIS FIELDS ARE STATES FROM PARENT COMPONENT
  setCoordinates = false,
  address,
  setAddress = () => {},
  //RESET FOR EDIT
  isEditingMarkerPosition,
  showInputAutocomplete = true,
  colonySelected,
  setColonySelected,
  handleCleanErrorMap = () => {},
}) => {
  const [currentMap, setCurrentMap] = useState(null)
  const [markerPosition, setMarkerPosition] = useState(null)
  const [autoComplete, setAutoComplete] = useState(null)
  const [geoZonesCoords, setGeoZonesCoords] = useState(null)
  const [geoColoniesCoords, setGeoColoniesCoords] = useState(null)
  const [subJurisdictions, setSubJurisdictions] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const hasFetchedLocation = useRef(false)

  const geocodeCache = useRef({})
  let lastClickTime = useRef(0)

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: API_KEY,
    libraries,
  })

  //Optener el catalog_jurisdiction_sub
  const {
    data: dataSubs,
    loading: loadingSubs,
    error: errorSubs,
  } = useQuery(
    GET_ALL_JURISDICTIONS_AND_SUBS,
    { variables: { jurisdiction_id: 1 } },
    { fetchPolicy: 'no-cache' }
  )

  const onLoad = async (mapInstance) => {
    setCurrentMap(mapInstance)
  }

  const geocodeLatLng = async (lat, lng) => {
    //COMPRUEBA SI LA DIRECCIÓN YA ESTÁ EN CACHE
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`
    if (geocodeCache.current[key]) {
      return geocodeCache.current[key]
    }
    const geocoder = new window.google.maps.Geocoder()
    const latLng = new window.google.maps.LatLng(lat, lng)

    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: latLng }, (results, status) => {
        switch (status) {
          case 'OK':
            if (!results[0]) {
              geocodeCache.current[key] = results[0] // cachearlo
              return reject(new Error('No se encontró la dirección'))
            }
            geocodeCache.current[key] = results[0]
            resolve(results[0])
            break
          case 'ZERO_RESULTS':
            handleErrorGoogleMap()
            reject(new Error('No se encontraron resultados para la ubicación'))
            break
          default:
            handleErrorGoogleMap()
            reject(new Error(`Geocoder falló debido a: ${status}`))
            break
        }
      })
    })
  }

  const getLocationFromLatLng = async (lat, lng) => {
    try {
      let isInZone = false
      let zoneId = null
      let zoneName = ''
      let colony_name = null
      let colony_number = null

      if (geoZonesCoords && geoZonesCoords.length > 0) {
        for (const item of geoZonesCoords) {
          const polygon = new google.maps.Polygon({ paths: item.coords })
          if (
            google.maps.geometry.poly.containsLocation(
              new google.maps.LatLng(lat, lng),
              polygon
            )
          ) {
            isInZone = true
            zoneId = item.zone?.id || null
            zoneName = item.zone?.name || ''
            break // Detener la iteración al encontrar la primera coincidencia
          }
        }
      }

      if (!isInZone) {
        handleErrorGoogleMap()
        return {
          error: true,
          message:
            'La ubicación seleccionada no está dentro de una zona válida',
        }
      }

      if (geoColoniesCoords && geoColoniesCoords.length > 0) {
        for await (const item of geoColoniesCoords) {
          const polygon = new google.maps.Polygon({ paths: item.coords })
          if (
            google.maps.geometry.poly.containsLocation(
              new google.maps.LatLng(lat, lng),
              polygon
            )
          ) {
            colony_number = item?.nocolonia || null //nocolonia
            colony_name = item?.name || null //name
            break // Detener la iteración al encontrar la primera coincidencia
          }
        }
      }

      const place = await geocodeLatLng(lat, lng)
      const url = `https://www.google.com/maps?q=${lat},${lng}`

      unstable_batchedUpdates(() => {
        setMarkerPosition({ lat, lng })
        if (setCoordinates) setCoordinates({ lat, lng })

        if (setAddress) {
          setAddress({
            ...place,
            url,
            zone_id: zoneId,
            zone_name: zoneName,
            colony_name,
            colony_number,
          })
        }
      })
    } catch (error) {
      handleErrorGoogleMap()
      return {
        error: true,
        message: error.message || 'Error al obtener la ubicación',
      }
    }
  }

  const handleClickMap = async (e) => {
    const now = Date.now()
    if (now - lastClickTime.current < 1000) {
      return // ignorar si pasó menos de 1 seg
    }
    lastClickTime.current = now

    if (!showInputAutocomplete) return
    if (!e.latLng) {
      handleErrorGoogleMap()
      return handleAlertError('No se encontró la ubicación')
    }
    if (setColonySelected) setColonySelected(null)
    return getLocationFromLatLng(e.latLng.lat(), e.latLng.lng())
  }

  const getProcessGeoJson = async () => {
    await currentMap.data.loadGeoJson(urlGeoJson, null, async (features) => {
      const dataZonesCoords = []

      const bounds = new google.maps.LatLngBounds() // Crear límites

      for await (const feature of features) {
        const zoneName = feature.getProperty('nombre')
        const coords = feature
          .getGeometry()
          .getArray()[0]
          .getArray()[0]
          .getArray()
          .map((item) => new google.maps.LatLng(item.lat(), item.lng()))

        const findZone = subJurisdictions.find((item) => item.name === zoneName)

        if (findZone) {
          dataZonesCoords.push({
            zone: {
              id: findZone.id,
              name: zoneName,
              strokeColor: findZone?.strokeColor ?? '#000000',
              fillColor: findZone?.fillColor ?? '#ff8819',
            },
            coords,
            centroid: getPolygonCentroid(coords),
          })

          // Extender los límites con las coordenadas de la zona
          coords.forEach((coord) => bounds.extend(coord))
        }
      }

      setGeoZonesCoords(dataZonesCoords)

      if (dataZonesCoords && dataZonesCoords.length > 0) {
        // Aplicar los límites al Autocomplete
        if (autoComplete) {
          autoComplete.setBounds(bounds)
          autoComplete.setOptions({
            strictBounds: true, // Restringir resultados a los límites
            types: ['geocode'], // Solo direcciones
          })
        }
      }
    })

    // Estilo GeoJSON
    currentMap.data.setStyle({ strokeWeight: 0.3 })
  }

  const onPlaceChanged = async (place) => {
    if (!place || !place.geometry) {
      handleErrorGoogleMap()
      return handleAlertError('No se encontró ubicación')
    }

    const lat = place.geometry.location.lat()
    const lng = place.geometry.location.lng()
    const result = await getLocationFromLatLng(lat, lng, place)

    if (result?.error) {
      handleErrorGoogleMap()
      return handleAlertError(result.message)
    }

    if (setColonySelected) setColonySelected(null)
    setMarkerPosition({ lat, lng })
    currentMap && currentMap.panTo({ lat, lng })
  }

  const handleAlertError = (message) => {
    toastSweetAlert({ mode: 'error', message })
  }

  const getProcessGeoJsonColonies = async () => {
    try {
      if (!geoColoniesCoords) {
        fetch(urlGeoJsonColonies)
          .then((response) => response.json())
          .then(async (geojson) => {
            const dataColoniesCoords = []

            for await (const feature of geojson.features) {
              const geoCoords = feature.geometry.coordinates[0][0].map(
                (utm) => {
                  const [lng, lat] = proj4(utmZone13N, 'WGS84', [
                    utm[0],
                    utm[1],
                  ])
                  return { lat, lng }
                }
              )

              dataColoniesCoords.push({
                nocolonia: feature.properties.nocolonia,
                name: feature.properties.nombre,
                coords: geoCoords,
              })
            }

            setGeoColoniesCoords(dataColoniesCoords)
          })
          .catch((error) => console.error('Error al cargar el GeoJSON:', error))
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (!geoColoniesCoords) getProcessGeoJsonColonies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoColoniesCoords])

  useEffect(() => {
    if (!loadingSubs) {
      const list = []
      dataSubs?.getAllJurisdictionsAndSubs.map((jurisdiction) => {
        jurisdiction.SubsOfJurisdiction.map((sub) =>
          list.push({
            id: sub?.id,
            name: sub?.name,
          })
        )
      })
      setSubJurisdictions(list)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSubs, dataSubs, errorSubs])

  useEffect(() => {
    console.log('ENTRO')
    if (!isEditingMarkerPosition) return

    if (isEditingMarkerPosition && geoZonesCoords && geoColoniesCoords) {
      if (isEditingMarkerPosition === '0,0') return
      if (!isEditingMarkerPosition.includes(',')) return

      const splitLatLng = isEditingMarkerPosition.split(',')
      const lat = parseFloat(splitLatLng[0])
      const lng = parseFloat(splitLatLng[1])
      return getLocationFromLatLng(lat, lng)
    }
  }, [isEditingMarkerPosition, geoZonesCoords, geoColoniesCoords])

  // useEffect(() => {
  //   if (hasFetchedLocation.current) return
  //   if (!isEditingMarkerPosition) return
  //   if (!geoZonesCoords || !geoColoniesCoords) return
  //   if (isEditingMarkerPosition === '0,0') return
  //   if (!isEditingMarkerPosition.includes(',')) return

  //   hasFetchedLocation.current = true

  //   const [lat, lng] = isEditingMarkerPosition.split(',').map(Number)
  //   getLocationFromLatLng(lat, lng)
  // }, [isEditingMarkerPosition, geoZonesCoords, geoColoniesCoords])

  useEffect(() => {
    if (currentMap && subJurisdictions?.length > 0) {
      getProcessGeoJson().then(() => {
        setIsReady(true)
      })
    }
  }, [currentMap, subJurisdictions])

  // useEffect(() => {
  //   if (currentMap) {
  //     const zoomListener = currentMap.addListener('zoom_changed', () => {
  //       setZoomLevel(currentMap.getZoom())
  //     })
  //     return () => {
  //       google.maps.event.removeListener(zoomListener)
  //     }
  //   }
  // }, [currentMap])

  useEffect(() => {
    if (colonySelected?.value && currentMap && geoColoniesCoords) {
      const foundColonyCoords = geoColoniesCoords.find(
        (colony) => colony.name === colonySelected.label
      )
      if (foundColonyCoords) {
        const centroid = getPolygonCentroidLatLng(foundColonyCoords.coords)
        return getLocationFromLatLng(centroid.lat, centroid.lng)
      } else {
        handleErrorGoogleMap()
        handleAlertError('No se encontró la colonia seleccionada')
      }
    }
  }, [colonySelected, currentMap, geoColoniesCoords])

  if (loadError) {
    return (
      <div>
        <div className="alert alert-default-warning">Error al cargar mapa</div>
      </div>
    )
  }
  if (!isLoaded || !subJurisdictions) return <Loader size="2rem" />
  if (subJurisdictions.length === 0) {
    return (
      <div>
        <div className="alert alert-default-warning">
          No hay zonas registradas
        </div>
      </div>
    )
  }

  const handleErrorGoogleMap = () => {
    setAddress(null)
    setCoordinates(null)
    document.getElementById('address_places').value = ''
    setMarkerPosition(null)
    handleCleanErrorMap()
  }

  return (
    <>
      <div
      // style={{
      //   width: '100%',
      //   padding: '20px',
      //   margin: '20px 0',
      //   borderRadius: '5px',
      //   border: '1px solid #d8d8d8',
      // }}
      >
        {showInputAutocomplete && (
          <GooglePlacesSearch
            disabled={false}
            addressFull={address?.formatted_address}
            onAddressChange={setAddress}
            onPlaceSelected={onPlaceChanged}
            name="address_places"
            listenOnChange={() => {
              setCoordinates(null)
              if (setColonySelected) setColonySelected('')
              setMarkerPosition(null)
            }}
          />
        )}
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={markerPosition || defaultCenterGoogleMap}
          zoom={markerPosition ? 15 : 14}
          options={{
            ...optionsGoogleMap,
            styles: stylesGoogleMap,
          }}
          clickableIcons
          onLoad={onLoad}
        >
          {markerPosition && <Marker position={markerPosition} />}

          {/* Poligonos de las zonas  */}
          {geoZonesCoords?.map((item, index) => (
            <GoogleMapZonePolygon
              item={item}
              index={index}
              handleClickMap={handleClickMap}
            />
          ))}
        </GoogleMap>
      </div>
    </>
  )
}

const getPolygonCentroid = (coords) => {
  let area = 0
  let lat = 0
  let lng = 0

  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length
    const xi = coords[i].lat()
    const yi = coords[i].lng()
    const xj = coords[j].lat()
    const yj = coords[j].lng()
    const a = xi * yj - xj * yi
    area += a
    lat += (xi + xj) * a
    lng += (yi + yj) * a
  }

  area /= 2
  lat /= 6 * area
  lng /= 6 * area

  return { lat, lng }
}

const getPolygonCentroidLatLng = (coords) => {
  let area = 0
  let lat = 0
  let lng = 0

  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length
    const xi = coords[i].lat
    const yi = coords[i].lng
    const xj = coords[j].lat
    const yj = coords[j].lng
    const a = xi * yj - xj * yi
    area += a
    lat += (xi + xj) * a
    lng += (yi + yj) * a
  }

  area /= 2
  lat /= 6 * area
  lng /= 6 * area

  return { lat, lng }
}

export default GoogleMapComponentV2
